/**
 * Restored in Review-Lauf 03 (CRITICAL, Linse A/B): deleted in Review-Lauf 01
 * as apparently-dead code during the Quill removal (it WAS the only caller of
 * `_onImageUpload` in the old `ha-rte.component.ts`, but the deletion took the
 * downscaling itself out with it, without a replacement in the new engine —
 * every image pasted/picked into `ha-rte` since then embeds raw, uncompressed
 * as inline base64, unbounded). Unchanged from before; wired back in via
 * `ha-rte`'s `imageUploadHandler`.
 */
export class ImageHelper {

    /**
     * The size an image gets so that it fits INSIDE the box in both dimensions, keeping its ratio.
     *
     * Pulled out of reducePixelsFn on 2026-09-18 so it can be tested at all: the rest of that method needs
     * an Image and a canvas, and vitest runs on environment 'node'. The browser half is guarded by a
     * Playwright spec instead.
     *
     * THE DEFECT THIS REPLACES: the previous arithmetic capped only the LONGER side. Measured through the
     * real handler in the browser, 2500×2000 came out as 1920×1536 — 456 pixels taller than the box. It
     * is not theoretical: of 529 images stored in the six local tenants, five are above the box, and every
     * one of them has exactly that shape (1920×1280, twice 1920×1440, twice 1920×1146) — width capped,
     * height left over.
     *
     * An image that already fits is returned unchanged, so nothing is scaled up and nothing is nudged by
     * a rounding step.
     */
    public static fitInside(
        width: number,
        height: number,
        maxWidth = 1920,
        maxHeight = 1080
    ): { width: number; height: number } {
        // One factor from BOTH ratios; never above 1, so a small image is left alone.
        const factor = Math.min(1, maxWidth / width, maxHeight / height);

        if (factor === 1) {
            return {width, height};
        }

        // Rounded down, never to zero: a very wide, very flat image must keep at least one pixel.
        return {
            width: Math.max(1, Math.floor(width * factor)),
            height: Math.max(1, Math.floor(height * factor)),
        };
    }

    /**
     * Turns a data URL back into a file, so the downscaled image can be uploaded instead of embedded.
     *
     * Decoded with atob rather than fetched: a `fetch` of a data: URL drags the network stack into a pure
     * conversion, and the e2e browser does not run in a secure context, where parts of that stack behave
     * differently. atob, Uint8Array and File are all available in the browser and in node, so this is
     * testable without a browser.
     */
    public static dataUrlToFile(dataUrl: string, fileName: string): File {
        const comma = dataUrl.indexOf(',');
        const header = dataUrl.slice(0, comma);

        if (!dataUrl.startsWith('data:') || comma === -1 || !header.includes(';base64')) {
            throw new Error('Not a base64 data URL');
        }

        const mime = header.slice('data:'.length, header.indexOf(';'));
        const binary = atob(dataUrl.slice(comma + 1));
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return new File([bytes], fileName, {type: mime});
    }

    public static async reducePixelsFn(
        base64Str: string,
        maxWidth = 1920,
        maxHeight = 1080,
        quality = .75
    ): Promise<string> {
        try {
            // Validate base64 string
            if (!base64Str.startsWith('data:image')) {
                throw new Error('Invalid image format');
            }

            const img = new Image();
            img.src = base64Str;

            // Handle loading errors
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => reject(new Error('Failed to load image'));
            });

            const canvas = document.createElement('canvas');
            const {width, height} = ImageHelper.fitInside(img.width, img.height, maxWidth, maxHeight);

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Failed to create canvas context');
            }

            ctx.drawImage(img, 0, 0, width, height);
            return canvas.toDataURL('image/webp', quality);
        } catch (error) {
            // Everything in the try block throws an Error (or a DOMException, which extends
            // Error), so the caught value carries a message.
            throw new Error(`Image processing failed: ${(error as Error).message}`);
        }
    }


}
